import { useLocation } from "react-router-dom";

/**
 * The products-only storefront a practice links to from its own site: the product pages, cart and
 * checkout mounted a second time under one prefix, with none of the app's navigation around them.
 */
export const STANDALONE_PRODUCTS_PATH = "/sa/products";

export const standaloneProductsPaths = {
  home: STANDALONE_PRODUCTS_PATH,
  browse: `${STANDALONE_PRODUCTS_PATH}/browse`,
  product: `${STANDALONE_PRODUCTS_PATH}/product`,
  cart: `${STANDALONE_PRODUCTS_PATH}/cart`,
  checkout: `${STANDALONE_PRODUCTS_PATH}/cart/checkout`,
} as const;

/**
 * The storefront opens straight on the product list, so the in-app list and the app pages the
 * storefront has no counterpart for all resolve to its home.
 */
const IN_APP_PATHS_SHOWN_AS_HOME = new Set(["/products/list", "/shop", "/home"]);
/** The products landing — collections and the brand, category, concern and area index. */
const IN_APP_PRODUCTS_LANDING_PATH = "/shop/products";
const IN_APP_PRODUCT_PDP_PREFIX = "/pdp/product/";
/** The cart pages the storefront mounts; the reward and financing-return pages stay in the app. */
const IN_APP_CART_PATHS_MOUNTED = new Set([
  "/cart",
  "/cart/checkout",
  "/cart/checkout/element",
  "/cart/checkout/success",
]);

export const isStandaloneProductsPath = (pathname: string) =>
  pathname === STANDALONE_PRODUCTS_PATH || pathname.startsWith(`${STANDALONE_PRODUCTS_PATH}/`);

/**
 * Where the storefront keeps the page an in-app path names. Paths it has no counterpart for —
 * a treatment or membership in a mixed cart, say — come back unchanged and open in the app.
 */
export const toStandaloneProductsPath = (inAppPath: string) => {
  const suffixStart = inAppPath.search(/[?#]/);
  const pathname = suffixStart === -1 ? inAppPath : inAppPath.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? "" : inAppPath.slice(suffixStart);

  if (IN_APP_PATHS_SHOWN_AS_HOME.has(pathname)) return `${standaloneProductsPaths.home}${suffix}`;
  if (pathname === IN_APP_PRODUCTS_LANDING_PATH)
    return `${standaloneProductsPaths.browse}${suffix}`;
  if (pathname.startsWith(IN_APP_PRODUCT_PDP_PREFIX)) {
    const productPath = pathname.slice(IN_APP_PRODUCT_PDP_PREFIX.length);
    return `${standaloneProductsPaths.product}/${productPath}${suffix}`;
  }
  if (IN_APP_CART_PATHS_MOUNTED.has(pathname)) {
    return `${STANDALONE_PRODUCTS_PATH}${pathname}${suffix}`;
  }
  return inAppPath;
};

const keepInAppPath = (inAppPath: string) => inAppPath;

/**
 * Resolves the in-app paths the shared product, cart and checkout pages name against the flow the
 * shopper is in, so a page rendered inside the storefront keeps its links there.
 */
export const shopFlowPathFrom = (currentPathname: string) =>
  isStandaloneProductsPath(currentPathname) ? toStandaloneProductsPath : keepInAppPath;

/** The loader form of `useShopFlowPath`. */
export const shopFlowPathForRequest = (request: Request) =>
  shopFlowPathFrom(new URL(request.url).pathname);

export const useIsStandaloneProducts = () => isStandaloneProductsPath(useLocation().pathname);

export const useShopFlowPath = () => shopFlowPathFrom(useLocation().pathname);
